import {
  ActionIcon,
  Box,
  createStyles,
  Modal,
  ModalProps,
} from '@mantine/core';
import { useFocusTrap } from '@mantine/hooks';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { ReactComponent as CloseX } from '@portals/icons/linear/close-x.svg';
import { useIsModalOpen } from '@portals/redux';

import { useRouteModalConfig } from './route-modals.hooks';
import {
  getBackgroundPath,
  RouteOverlayLocationState,
  RouteOverlaysProvider,
} from '../route-overlays';

interface RouteModalProps extends Partial<ModalProps> {
  modalId: string;
}

export function RouteModal({
  children,
  modalId,
  ...modalProps
}: RouteModalProps) {
  const { classes } = useModalStyles();
  const history = useHistory<RouteOverlayLocationState>();
  const defaultBackgroundPath = useRouteModalConfig(modalId).backgroundPath;

  const isModalOpen = useIsModalOpen();
  const focusTrapRef = useFocusTrap(!isModalOpen);

  const onClose = useCallback(() => {
    const backgroundPath = getBackgroundPath(
      history.location,
      defaultBackgroundPath
    );

    history.push(backgroundPath);
  }, [defaultBackgroundPath, history]);

  return (
    <RouteOverlaysProvider onClose={onClose}>
      <Modal
        opened
        onClose={onClose}
        closeOnClickOutside={false}
        size="100%"
        classNames={{
          inner: classes.inner,
          content: classes.content,
          header: classes.header,
          body: classes.body,
        }}
        radius="md"
        {...modalProps}
      >
        <ActionIcon className={classes.closeIcon} onClick={onClose}>
          <CloseX width={18} height={18} data-testid="modal-close-btn" />
        </ActionIcon>

        <Box ref={focusTrapRef} sx={{ width: '100%', height: '100%' }}>
          {children}
        </Box>
      </Modal>
    </RouteOverlaysProvider>
  );
}

const useModalStyles = createStyles((theme) => ({
  inner: {
    padding: theme.spacing.xl,
  },
  content: {
    height: '100%',
    maxHeight: '100%',
  },
  header: {
    display: 'none',
  },
  body: {
    padding: 0,
    height: '100%',
    position: 'relative',
  },
  closeIcon: {
    position: 'absolute',
    top: theme.spacing.xl,
    right: theme.spacing.xl,
    zIndex: 1,
  },
}));
